import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import {PrismicRichText} from '@prismicio/react'
import {useLinkResolver} from 'prismicio'
import type {ButtonDocument, PricingSectionSliceModulesAndAddonsPrimaryCtaSectionItem} from 'prismicio-types'
import {ButtonV2} from 'src/common/ButtonV2'
import {getButtonsData} from 'src/common/helpers'

type CTASectionModulesAndAddonsProps = {
  ctaSections: PricingSectionSliceModulesAndAddonsPrimaryCtaSectionItem[]
  pageUid: string
  componentIndex: number
  buttonsData?: ButtonDocument[]
}

export const CTASectionModulesAndAddons = ({
  ctaSections,
  pageUid,
  componentIndex,
  buttonsData,
}: CTASectionModulesAndAddonsProps) => {
  const {linkResolver} = useLinkResolver()

  if (ctaSections.length === 0) {
    return null
  }

  return (
    <div className="flex flex-col items-center gap-6">
      {ctaSections.map((cta, index) => {
        const {primaryButtonData} = getButtonsData({
          buttonsData,
          primaryButton: cta.primary_button,
        })

        return (
          <div key={`cta-${index}`} className="flex flex-col items-center gap-6 text-center">
            {/* Capterra Badge */}
            {(isFilled.image(cta.tag_icon) || isFilled.keyText(cta.tag_text)) && (
              <div className="flex items-center gap-2 rounded-full border border-grey-135 bg-moss-white px-3 py-1">
                {isFilled.image(cta.tag_icon) && (
                  <div className="flex size-5 items-center justify-center rounded-full bg-moss-black">
                    <PrismicNextImage field={cta.tag_icon} className="size-3" />
                  </div>
                )}
                {isFilled.keyText(cta.tag_text) && <p className="text-body-bold text-moss-black">{cta.tag_text}</p>}
              </div>
            )}

            {/* Heading and Subtext */}
            <div className="flex flex-col gap-2">
              {isFilled.richText(cta.heading) && (
                <PrismicRichText
                  field={cta.heading}
                  components={{
                    heading2: ({children}) => <h2 className="text-h4 text-moss-black">{children}</h2>,
                  }}
                />
              )}
              {isFilled.richText(cta.subtext) && (
                <PrismicRichText
                  field={cta.subtext}
                  components={{
                    paragraph: ({children}) => <p className="text-tg-body-l text-grey-550">{children}</p>,
                  }}
                />
              )}
            </div>

            {/* Button */}
            {primaryButtonData && (
              <ButtonV2
                variant="primary"
                href={linkResolver(primaryButtonData.link, 'cta-button')}
                trackingData={{
                  location: `component-${componentIndex}-pricing-cta`,
                  dataType: 'cta-button',
                  pageName: pageUid,
                  mktgId: primaryButtonData.marketing_id || '',
                  label: primaryButtonData.label!,
                }}
                newTrackingProps={{
                  interaction: {
                    element: primaryButtonData?.marketing_id || 'primary-button-pricing-section-modules-and-addons',
                    elementText: primaryButtonData?.label || '',
                    location: `component-${componentIndex}-pricing-section-modules-and-addons`,
                  },
                  pageType: pageUid || 'unknown',
                }}
                id={`component-${componentIndex}-pricing-cta`}
                className="uppercase"
              >
                {primaryButtonData.label}
              </ButtonV2>
            )}
          </div>
        )
      })}
    </div>
  )
}
